import { Layout } from '@/components/Layout';
import {
  Target,
  MessageCircle,
  TrendingUp,
  Users,
  FileText,
  PlayCircle,
  LucideIcon,
} from 'lucide-react';

/* ─── Brand tokens ─── */
const C = {
  navy:      '#081A2D',
  royal:     '#112D82',
  sky:       '#78B0E3',
  amber:     '#F4A35B',
  white:     '#FFFFFF',
  card:      '#0D2240',
  cardLight: '#0E2A45',
  border:    'rgba(120,176,227,0.10)',
  borderAmber: 'rgba(244,163,91,0.22)',
  body:      '#B8CEDF',
  muted:     '#5E7A95',
  display:   "'DM Serif Display', Georgia, serif",
  sans:      "'DM Sans', -apple-system, sans-serif",
};
const WA = 'https://chat.whatsapp.com/DeAfc79sKr2AdppE1xJafE';

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
function hoverCta(el: HTMLElement, on: boolean) {
  el.style.backgroundColor = on ? '#f8b878' : C.amber;
  el.style.transform = on ? 'translateY(-2px)' : 'none';
}

/* ─── Sub-components ─── */
function CheckMark() {
  return (
    <div style={{ width: 18, height: 18, minWidth: 18, borderRadius: '50%', border: `1px solid ${C.borderAmber}`, backgroundColor: 'rgba(244,163,91,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2 }}>
      <svg viewBox="0 0 12 12" fill="none" width="10" height="10">
        <path d="M2 6l2.5 2.5L10 3.5" stroke={C.amber} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

function SkyLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: C.sans, fontSize: 10, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase' as const, color: C.sky, marginBottom: 14 }}>
      <span style={{ width: 20, height: 1, backgroundColor: C.sky, opacity: 0.4, display: 'inline-block' }} />
      {children}
    </div>
  );
}

function AmberPill({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 18px', borderRadius: 50, border: `1px solid ${C.borderAmber}`, backgroundColor: 'rgba(244,163,91,0.07)', fontFamily: C.sans, fontSize: 10, fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' as const, color: C.amber, marginBottom: 28 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: C.amber }} />
      {children}
    </div>
  );
}

function Divider() {
  return <div style={{ height: 1, background: `linear-gradient(90deg,transparent,rgba(120,176,227,0.10),transparent)`, maxWidth: 960, margin: '0 auto' }} />;
}

function IconBadge({ Icon, color, bg }: { Icon: LucideIcon; color: string; bg: string }) {
  return (
    <div style={{ width: 44, height: 44, borderRadius: 10, backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, flexShrink: 0 }}>
      <Icon size={22} color={color} strokeWidth={1.75} />
    </div>
  );
}

/* ─── Format cards data ─── */
type FormatCard = { Icon: LucideIcon; iconColor: string; iconBg: string; title: string; desc: string; freq: string; };
const FORMAT_CARDS: FormatCard[] = [
  { Icon: Target,        iconColor: C.amber,    iconBg: 'rgba(244,163,91,0.12)',   title: '2 encontros estratégicos ao vivo',     desc: 'Posicionamento e geração de demanda. Rafael e Eduardo conduzem ao vivo, com casos reais e frameworks aplicáveis. Gravações disponíveis.',                                              freq: '2× / mês · 90 min cada · Google Meet' },
  { Icon: MessageCircle, iconColor: C.sky,      iconBg: 'rgba(120,176,227,0.12)',  title: 'Q&A aberto mensal',                    desc: 'Um encontro dedicado só a perguntas. Nada de slides — você traz o que está travando, e a gente resolve ao vivo com o grupo.',                                                    freq: '1× / mês · 60 min · sem roteiro fixo' },
  { Icon: TrendingUp,    iconColor: '#6EE4A8',  iconBg: 'rgba(110,228,168,0.10)', title: 'Treinamento comercial em grupo',       desc: 'Scripts de vendas, abordagem por WhatsApp, tratamento de objeções — treinamento prático junto com todos os clientes da ClinoGrow.',                                          freq: 'Quinzenal · com consultor comercial' },
  { Icon: Users,         iconColor: '#A78BFA',  iconBg: 'rgba(167,139,250,0.10)', title: 'Grupo exclusivo de network',           desc: 'Comunidade fechada com todos os membros ativos — troca de fornecedores, indicações entre especialidades e benchmarking de métricas em tempo real.',                              freq: 'WhatsApp · acesso imediato' },
  { Icon: FileText,      iconColor: C.amber,    iconBg: 'rgba(244,163,91,0.12)',   title: 'Templates e frameworks',               desc: 'Materiais práticos atualizados mensalmente — scripts, planilhas de metas, checklists e modelos de proposta prontos para usar.',                                                freq: 'Atualizado mensalmente' },
  { Icon: PlayCircle,    iconColor: C.sky,      iconBg: 'rgba(120,176,227,0.12)',  title: 'Acesso às gravações',                  desc: 'Todos os encontros ao vivo ficam disponíveis no repositório do grupo. Perdeu uma sessão ou quer rever — está lá.',                                                            freq: 'Repositório atualizado a cada encontro' },
];

/* ─── Page ─── */
export default function Home() {
  return (
    <Layout>

      {/* ═══ HERO ═══ */}
      <section id="hero" style={{ minHeight: '100vh', backgroundColor: C.navy, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: `radial-gradient(ellipse 55% 65% at 85% 45%,rgba(17,45,130,0.22) 0%,transparent 60%),radial-gradient(ellipse 40% 40% at 5% 90%,rgba(17,45,130,0.12) 0%,transparent 55%)` }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,transparent,${C.royal},transparent)`, opacity: 0.4 }} />

        <div style={{ maxWidth: 1160, margin: '0 auto', padding: '140px 48px 80px', width: '100%' }}>
          <div className="hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: 64, alignItems: 'center' }}>

            {/* Left — copy */}
            <div>
              <AmberPill>Grupo fechado · Vagas limitadas</AmberPill>
              <h1 style={{ fontFamily: C.display, fontSize: 'clamp(36px,4.5vw,56px)', color: C.white, lineHeight: 1.1, marginBottom: 22, fontWeight: 400 }}>
                O lugar onde donos de clínica param de{' '}
                <em style={{ fontStyle: 'italic', color: C.amber }}>adivinhar</em>{' '}
                e começam a decidir.
              </h1>
              <p style={{ fontFamily: C.sans, fontSize: 17, color: C.body, lineHeight: 1.8, marginBottom: 40, maxWidth: 540 }}>
                O Mastermind ClinoGrow é o único espaço onde médicos e donos de clínica trocam experiências reais — faturamento, CAC, churn, estratégia — com quem enfrenta os mesmos desafios.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <a href={WA} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', padding: '14px 32px', borderRadius: 6, backgroundColor: C.amber, color: C.navy, fontFamily: C.sans, fontSize: 15, fontWeight: 700, textDecoration: 'none', transition: 'all 0.2s' }}
                  onMouseEnter={e => hoverCta(e.currentTarget as HTMLElement, true)}
                  onMouseLeave={e => hoverCta(e.currentTarget as HTMLElement, false)}>
                  Quero uma vaga
                </a>
                <button onClick={() => scrollTo('formato')}
                  style={{ display: 'inline-flex', alignItems: 'center', padding: '14px 32px', borderRadius: 6, backgroundColor: 'transparent', color: C.white, fontFamily: C.sans, fontSize: 15, fontWeight: 500, border: `1px solid ${C.border}`, transition: 'all 0.2s', cursor: 'pointer' }}
                  onMouseEnter={e => ((e.currentTarget as HTMLElement).style.borderColor = 'rgba(120,176,227,0.35)')}
                  onMouseLeave={e => ((e.currentTarget as HTMLElement).style.borderColor = C.border)}>
                  Ver o formato
                </button>
              </div>
            </div>

            {/* Right — price card */}
            <div>
              <div style={{ backgroundColor: C.card, border: `1px solid ${C.borderAmber}`, borderRadius: 14, padding: '36px 32px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${C.amber},rgba(244,163,91,0.3),transparent)` }} />
                <p style={{ fontFamily: C.sans, fontSize: 10, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.muted, marginBottom: 8 }}>Investimento</p>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
                  <span style={{ fontFamily: C.sans, fontSize: 18, color: C.muted }}>R$</span>
                  <span style={{ fontFamily: C.display, fontSize: 52, color: C.white, lineHeight: 1 }}>1.500</span>
                </div>
                <p style={{ fontFamily: C.sans, fontSize: 13, color: C.muted, marginBottom: 26 }}>/mês · compromisso anual (R$ 18.000 total)</p>
                <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28 }}>
                  {['2 encontros ao vivo por mês (90 min cada)', '1 Q&A aberto mensal com Rafael e Eduardo', 'Treinamento comercial quinzenal em grupo', 'Grupo exclusivo de network (WhatsApp)', 'Templates e frameworks atualizados mensalmente'].map(item => (
                    <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <CheckMark />
                      <span style={{ fontFamily: C.sans, fontSize: 13, color: C.body, lineHeight: 1.55 }}>{item}</span>
                    </li>
                  ))}
                </ul>
                <a href={WA} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'block', textAlign: 'center', padding: '13px 24px', borderRadius: 6, backgroundColor: C.amber, color: C.navy, fontFamily: C.sans, fontSize: 14, fontWeight: 700, textDecoration: 'none', transition: 'background 0.2s', marginBottom: 20 }}
                  onMouseEnter={e => ((e.currentTarget as HTMLElement).style.backgroundColor = '#f8b878')}
                  onMouseLeave={e => ((e.currentTarget as HTMLElement).style.backgroundColor = C.amber)}>
                  Quero minha vaga
                </a>
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16, fontFamily: C.sans, fontSize: 11, color: C.muted, textAlign: 'center', lineHeight: 1.65 }}>
                  Capacidade máxima: <strong style={{ color: C.amber }}>30 clínicas por turma</strong>.<br />
                  Ao atingir o limite, abre nova turma.
                </div>
              </div>
            </div>
          </div>
        </div>

        <style>{`@media(max-width:900px){.hero-grid{grid-template-columns:1fr!important}.hero-grid>div:last-child{display:none!important}}`}</style>
      </section>

      <Divider />

      {/* ═══ QUOTE ═══ */}
      <section style={{ backgroundColor: C.navy, padding: '80px 48px' }}>
        <div style={{ maxWidth: 820, margin: '0 auto', backgroundColor: C.card, border: `1px solid ${C.borderAmber}`, borderRadius: 14, padding: '48px 52px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg,transparent,${C.amber},transparent)`, opacity: 0.3 }} />
          <div style={{ position: 'absolute', top: 16, left: 32, fontFamily: C.display, fontSize: 80, color: 'rgba(244,163,91,0.12)', lineHeight: 1, userSelect: 'none', pointerEvents: 'none' }}>"</div>
          <blockquote style={{ fontFamily: C.display, fontSize: 'clamp(20px,2.5vw,26px)', color: C.white, lineHeight: 1.45, paddingLeft: 16, marginBottom: 24, position: 'relative', zIndex: 1 }}>
            O Mastermind não é apenas uma mentoria em grupo. É o único espaço onde donos de clínicas do setor de saúde se reúnem para trocar experiências reais, resolver problemas comuns e construir relacionamentos profissionais duradouros.{' '}
            <em style={{ fontStyle: 'italic', color: C.amber }}>O network é o produto — a mentoria é o formato de entrega.</em>
          </blockquote>
          <cite style={{ fontFamily: C.sans, fontSize: 13, color: C.muted, paddingLeft: 16, fontStyle: 'normal' }}>— Rafael Marques, fundador da ClinoGrow</cite>
        </div>
      </section>

      <Divider />

      {/* ═══ FORMATO ═══ */}
      <section id="formato" style={{ backgroundColor: C.navy, padding: '88px 48px' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <SkyLabel>Formato</SkyLabel>
          <h2 style={{ fontFamily: C.display, fontSize: 'clamp(30px,4vw,44px)', color: C.white, lineHeight: 1.15, marginBottom: 16 }}>
            O que você <em style={{ fontStyle: 'italic', color: C.amber }}>recebe</em> todo mês
          </h2>
          <p style={{ fontFamily: C.sans, fontSize: 16, color: C.body, lineHeight: 1.8, marginBottom: 48, maxWidth: 560 }}>
            Estrutura pensada para donos de clínica com agenda lotada. Conteúdo denso, direto ao ponto, com aplicação imediata.
          </p>
          <div className="format-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
            {FORMAT_CARDS.map(({ Icon, iconColor, iconBg, title, desc, freq }) => (
              <div key={title}
                style={{ backgroundColor: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '26px 22px', transition: 'border-color 0.2s' }}
                onMouseEnter={e => ((e.currentTarget as HTMLElement).style.borderColor = C.borderAmber)}
                onMouseLeave={e => ((e.currentTarget as HTMLElement).style.borderColor = C.border)}>
                <IconBadge Icon={Icon} color={iconColor} bg={iconBg} />
                <h3 style={{ fontFamily: C.sans, fontSize: 14, fontWeight: 600, color: C.white, marginBottom: 10, lineHeight: 1.4 }}>{title}</h3>
                <p style={{ fontFamily: C.sans, fontSize: 13, color: C.muted, lineHeight: 1.65, marginBottom: 16 }}>{desc}</p>
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14, fontFamily: C.sans, fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: C.sky }}>{freq}</div>
              </div>
            ))}
          </div>
        </div>
        <style>{`@media(max-width:900px){.format-grid{grid-template-columns:1fr 1fr!important}}@media(max-width:600px){.format-grid{grid-template-columns:1fr!important}}`}</style>
      </section>

      <Divider />

      {/* ═══ NETWORK ═══ */}
      <section id="network" style={{ backgroundColor: C.navy, padding: '88px 48px' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <SkyLabel>O maior diferencial</SkyLabel>
          <h2 style={{ fontFamily: C.display, fontSize: 'clamp(30px,4vw,44px)', color: C.white, lineHeight: 1.15, marginBottom: 16 }}>
            O network que <em style={{ fontStyle: 'italic', color: C.amber }}>nenhuma agência</em> oferece
          </h2>
          <p style={{ fontFamily: C.sans, fontSize: 16, color: C.body, lineHeight: 1.8, marginBottom: 48, maxWidth: 560 }}>
            Donos de clínica raramente encontram outros pares dispostos a falar abertamente sobre faturamento, CAC e estratégia. Esse ambiente não existe naturalmente. O Mastermind cria esse espaço.
          </p>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: C.cardLight, borderBottom: `1px solid ${C.borderAmber}` }}>
                  <th style={{ textAlign: 'left', padding: '14px 22px', fontFamily: C.sans, fontSize: 10, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: C.sky }}>O que os membros trocam</th>
                  <th style={{ textAlign: 'left', padding: '14px 22px', fontFamily: C.sans, fontSize: 10, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: C.sky }}>Impacto prático</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Fornecedores testados (equipamentos, sistemas, insumos)', 'Redução de custos operacionais sem pesquisa do zero'],
                  ['Indicação de pacientes entre especialidades não concorrentes', 'Nova fonte de receita sem custo de aquisição'],
                  ['Benchmarking real de métricas (ticket médio, conversão, churn)', 'Calibração das próprias metas com dados reais do setor'],
                  ['Parceiros para collabs de conteúdo e eventos', 'Crescimento de audiência compartilhado sem custo de mídia'],
                  ['Suporte de pares em crises operacionais', 'Decisão mais rápida, com menos erro e menos ansiedade'],
                ].map(([what, impact], i, arr) => (
                  <tr key={i} style={{ borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : 'none' }}
                    onMouseEnter={e => ((e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.015)')}
                    onMouseLeave={e => ((e.currentTarget as HTMLElement).style.backgroundColor = 'transparent')}>
                    <td style={{ padding: '18px 22px', fontFamily: C.sans, fontSize: 13, color: C.white, fontWeight: 500, lineHeight: 1.5, verticalAlign: 'middle' }}>{what}</td>
                    <td style={{ padding: '18px 22px', fontFamily: C.sans, fontSize: 13, color: C.amber, lineHeight: 1.5, verticalAlign: 'middle' }}>{impact}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <Divider />

      {/* ═══ FIT ═══ */}
      <section id="fit" style={{ backgroundColor: C.navy, padding: '88px 48px' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <SkyLabel>Fit</SkyLabel>
          <h2 style={{ fontFamily: C.display, fontSize: 'clamp(30px,4vw,44px)', color: C.white, lineHeight: 1.15, marginBottom: 16 }}>
            Para quem é — e para quem <em style={{ fontStyle: 'italic', color: C.amber }}>não é</em>
          </h2>
          <p style={{ fontFamily: C.sans, fontSize: 16, color: C.body, lineHeight: 1.8, marginBottom: 48, maxWidth: 560 }}>
            O Mastermind tem capacidade limitada por design. Não funciona com 50 pessoas. Reservamos vagas para quem está no momento certo.
          </p>
          <div className="fit-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {/* FOR */}
            <div style={{ backgroundColor: 'rgba(17,45,130,0.15)', border: '1px solid rgba(17,45,130,0.45)', borderRadius: 12, padding: '30px 28px' }}>
              <p style={{ fontFamily: C.sans, fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: '#6EE4A8', marginBottom: 20 }}>✓ É para você se...</p>
              <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 13 }}>
                {[
                  'Você fatura entre R$ 60k e R$ 300k/mês e quer crescer com método',
                  'Você está cansado de depender de indicação ou de agências que não entendem saúde',
                  'Você quer estruturar seu comercial e ter previsibilidade no faturamento',
                  'Você valoriza rede de qualidade e troca honesta com pares',
                  'Você quer crescer sem abrir mão da qualidade de vida',
                ].map(item => (
                  <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ color: '#6EE4A8', fontWeight: 700, fontSize: 15, lineHeight: '1.1', marginTop: 1 }}>+</span>
                    <span style={{ fontFamily: C.sans, fontSize: 13, color: C.body, lineHeight: 1.55 }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* NOT FOR */}
            <div style={{ backgroundColor: 'rgba(80,20,20,0.12)', border: '1px solid rgba(220,80,80,0.22)', borderRadius: 12, padding: '30px 28px' }}>
              <p style={{ fontFamily: C.sans, fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: '#F47070', marginBottom: 20 }}>✗ Não é para você se...</p>
              <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 13 }}>
                {[
                  'Você quer resultados sem aplicar o que aprende nas sessões',
                  'Você está buscando uma palestra motivacional, não uma comunidade de estratégia',
                  'Você ainda não tem uma clínica em operação',
                ].map(item => (
                  <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ color: '#F47070', fontWeight: 700, fontSize: 15, lineHeight: '1.1', marginTop: 1 }}>−</span>
                    <span style={{ fontFamily: C.sans, fontSize: 13, color: C.body, lineHeight: 1.55 }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <style>{`@media(max-width:640px){.fit-grid{grid-template-columns:1fr!important}}`}</style>
      </section>

      <Divider />

      {/* ═══ CTA FINAL ═══ */}
      <section id="cta" style={{ backgroundColor: C.navy, padding: '80px 48px 96px' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div style={{ backgroundColor: C.card, border: `1px solid ${C.borderAmber}`, borderRadius: 14, padding: '64px 48px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 60% 70% at 50% 50%,rgba(244,163,91,0.05) 0%,transparent 65%)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,transparent,${C.royal},transparent)`, opacity: 0.6 }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h2 style={{ fontFamily: C.display, fontSize: 'clamp(26px,3.5vw,40px)', color: C.white, lineHeight: 1.2, marginBottom: 18 }}>
                Pronto para entrar no <em style={{ fontStyle: 'italic', color: C.amber }}>grupo certo?</em>
              </h2>
              <p style={{ fontFamily: C.sans, fontSize: 16, color: C.body, lineHeight: 1.75, maxWidth: 480, margin: '0 auto 36px' }}>
                As vagas são limitadas a 30 clínicas por turma. Quando chegamos no limite, abrimos nova turma — sem exceção.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                <a href={WA} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '15px 40px', borderRadius: 6, backgroundColor: C.amber, color: C.navy, fontFamily: C.sans, fontSize: 15, fontWeight: 700, textDecoration: 'none', transition: 'all 0.2s' }}
                  onMouseEnter={e => hoverCta(e.currentTarget as HTMLElement, true)}
                  onMouseLeave={e => hoverCta(e.currentTarget as HTMLElement, false)}>
                  Quero minha vaga no Mastermind
                </a>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: C.sans, fontSize: 12, color: C.muted }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#F47070', display: 'inline-block' }} />
                Vagas limitadas · R$ 1.500/mês · compromisso anual
              </div>
            </div>
          </div>
        </div>
      </section>

    </Layout>
  );
}
